<template>
  <v-dialog v-model="dialogOpen" max-width="960" persistent>
    <v-card class="excel-modal-card">
      <div class="excel-modal-header">
        <v-icon size="22" class="mr-2" color="#4caf50">mdi-microsoft-excel</v-icon>
        <span class="excel-modal-title">시설 데이터 엑셀 다운로드</span>
        <v-spacer />
        <v-btn icon variant="text" size="small" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="excel-modal-tabs">
        <button
          type="button"
          class="excel-tab-btn"
          :class="{ active: excelTab === 'filter' }"
          @click="switchExcelTab('filter')"
        >
          <v-icon size="16" class="mr-1">mdi-filter-outline</v-icon>
          조건 설정
          <span v-if="activeFilterCount > 0" class="tab-badge">{{ activeFilterCount }}</span>
        </button>
        <button
          type="button"
          class="excel-tab-btn"
          :class="{ active: excelTab === 'columns' }"
          @click="switchExcelTab('columns')"
        >
          <v-icon size="16" class="mr-1">mdi-format-list-checks</v-icon>
          받을 내용
          <span class="tab-badge tab-badge--muted">{{ selectedColumns.length }}</span>
        </button>
      </div>

      <div class="excel-modal-body">
        <LoadingOverlay
          :show="downloadLoading"
          text="엑셀 생성 중..."
          variant="save"
        />

        <div v-show="excelTab === 'filter'" class="excel-tab-panel">
          <div class="filter-row">
            <div class="filter-label">검색</div>
            <div class="excel-search-field">
              <input
                v-model="excelFilters.search"
                type="text"
                class="excel-search-input"
                placeholder="시설명, 주소, 번호"
              />
            </div>
          </div>

          <div class="filter-row">
            <div class="filter-label">영업</div>
            <div class="filter-chips">
              <v-chip
                size="small"
                :class="{ 'chip-selected': !excelFilters.salesFilter }"
                @click="excelFilters.salesFilter = null"
              >
                전체
              </v-chip>
              <v-chip
                v-for="opt in salesFilterOptions"
                :key="opt.value"
                size="small"
                :class="{ 'chip-selected': excelFilters.salesFilter === opt.value }"
                @click="setSalesFilter(opt.value)"
              >
                {{ opt.label }}
              </v-chip>
            </div>
          </div>

          <div class="filter-row">
            <div class="filter-label">
              <v-icon size="18" class="mr-1">mdi-map-marker</v-icon>
              시도
            </div>
            <div class="filter-chips">
              <v-chip
                v-for="sido in sidoOptions"
                :key="sido.sido_code"
                :class="{ 'chip-selected': excelFilters.sidoCodes?.includes(sido.sido_code) }"
                size="small"
                @click="toggleSidoFilter(sido.sido_code)"
              >
                {{ sido.sido_name }}
              </v-chip>
              <v-btn variant="text" size="x-small" class="ml-2" @click="selectAllSido">
                {{ excelFilters.sidoCodes?.length === sidoOptions.length ? '전체 해제' : '전체 선택' }}
              </v-btn>
            </div>
          </div>

          <div v-if="selectedSidoForSigungu" class="filter-row filter-row--sigungu">
            <div class="filter-label">
              <v-icon size="18" class="mr-1">mdi-map-marker-outline</v-icon>
              시군구
            </div>
            <div class="filter-chips">
              <v-chip
                size="small"
                :class="{ 'chip-selected': !excelFilters.sigunguCode }"
                @click="clearSigunguFilter"
              >
                전체
              </v-chip>
              <v-chip
                v-for="sg in sigunguOptions"
                :key="sg.sigungu_code"
                :class="{ 'chip-selected': excelFilters.sigunguCode === sg.sigungu_code }"
                size="small"
                @click="toggleSigunguFilter(sg.sigungu_code)"
              >
                {{ sg.sigungu_name }}
              </v-chip>
            </div>
          </div>
          <p v-else-if="excelFilters.sidoCodes?.length > 1" class="excel-filter-hint excel-filter-hint--inline">
            <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>
            시군구는 시도를 하나만 선택했을 때 지정할 수 있습니다.
          </p>

          <div class="filter-row">
            <div class="filter-label">
              <v-icon size="18" class="mr-1">mdi-domain</v-icon>
              사업구분
            </div>
            <div class="filter-chips">
              <v-chip
                v-for="biz in excelBizOptions"
                :key="biz.value"
                :class="{ 'chip-selected': excelFilters.bizGubuns?.includes(biz.value) }"
                :style="{ '--chip-color': biz.color }"
                size="small"
                @click="toggleBizGubunFilter(biz.value)"
              >
                {{ biz.label }}
              </v-chip>
              <v-btn variant="text" size="x-small" class="ml-2" @click="selectAllBizGubun">
                {{ excelFilters.bizGubuns?.length === excelBizOptions.length ? '전체 해제' : '전체 선택' }}
              </v-btn>
            </div>
          </div>

          <div class="filter-row">
            <div class="filter-label">
              <v-icon size="18" class="mr-1">mdi-account-group</v-icon>
              직원 수
              <v-tooltip location="top">
                <template v-slot:activator="{ props: tooltipProps }">
                  <v-icon v-bind="tooltipProps" size="14" class="ml-1" color="grey">mdi-help-circle</v-icon>
                </template>
                <div class="staff-tooltip">
                  <div class="tooltip-title">직원 수에 포함되는 인력:</div>
                  <ul>
                    <li>시설장/관리자</li>
                    <li>사회복지사</li>
                    <li>간호사/간호조무사</li>
                    <li>요양보호사(1급/2급)</li>
                    <li>물리치료사/작업치료사</li>
                    <li>영양사/조리원</li>
                    <li>사무원/기타</li>
                  </ul>
                </div>
              </v-tooltip>
            </div>
            <div class="filter-range">
              <v-text-field
                v-model.number="excelFilters.staff_total_min"
                type="number"
                variant="outlined"
                density="compact"
                placeholder="최소"
                hide-details
                style="max-width: 100px;"
              />
              <span class="range-separator">~</span>
              <v-text-field
                v-model.number="excelFilters.staff_total_max"
                type="number"
                variant="outlined"
                density="compact"
                placeholder="최대"
                hide-details
                style="max-width: 100px;"
              />
              <span class="range-unit">명</span>
              <div class="quick-ranges">
                <v-chip size="x-small" @click="setStaffRange(1, 10)">1~10명</v-chip>
                <v-chip size="x-small" @click="setStaffRange(11, 30)">11~30명</v-chip>
                <v-chip size="x-small" @click="setStaffRange(31, 50)">31~50명</v-chip>
                <v-chip size="x-small" @click="setStaffRangeOver51">51명 이상</v-chip>
              </div>
            </div>
          </div>

          <div class="excel-advanced-toggle" @click="showAdvancedFilters = !showAdvancedFilters">
            <v-icon size="18" class="mr-1">mdi-tune</v-icon>
            추가 필터 (정원·현원·입소율·평가등급)
            <v-spacer />
            <v-icon size="18">{{ showAdvancedFilters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>

          <v-expand-transition>
            <div v-show="showAdvancedFilters" class="advanced-filters-grid excel-advanced-panel">
              <div class="adv-filter-item">
                <span class="adv-filter-label">정원</span>
                <v-text-field
                  v-model.number="excelFilters.total_capacity_min"
                  type="number"
                  variant="outlined"
                  density="compact"
                  placeholder="최소"
                  hide-details
                />
                <span>~</span>
                <v-text-field
                  v-model.number="excelFilters.total_capacity_max"
                  type="number"
                  variant="outlined"
                  density="compact"
                  placeholder="최대"
                  hide-details
                />
              </div>
              <div class="adv-filter-item">
                <span class="adv-filter-label">현원</span>
                <v-text-field
                  v-model.number="excelFilters.total_current_min"
                  type="number"
                  variant="outlined"
                  density="compact"
                  placeholder="최소"
                  hide-details
                />
                <span>~</span>
                <v-text-field
                  v-model.number="excelFilters.total_current_max"
                  type="number"
                  variant="outlined"
                  density="compact"
                  placeholder="최대"
                  hide-details
                />
              </div>
              <div class="adv-filter-item">
                <span class="adv-filter-label">입소율(%)</span>
                <v-text-field
                  v-model.number="excelFilters.occupancy_rate_min"
                  type="number"
                  variant="outlined"
                  density="compact"
                  placeholder="최소"
                  hide-details
                />
                <span>~</span>
                <v-text-field
                  v-model.number="excelFilters.occupancy_rate_max"
                  type="number"
                  variant="outlined"
                  density="compact"
                  placeholder="최대"
                  hide-details
                />
              </div>
              <div class="adv-filter-item">
                <span class="adv-filter-label">평가등급</span>
                <div class="eval-grade-chips">
                  <v-chip
                    v-for="grade in ['A', 'B', 'C', 'D', 'E']"
                    :key="grade"
                    :class="{ 'chip-selected': excelFilters.evalGrades?.includes(grade) }"
                    size="small"
                    @click="toggleEvalGradeFilter(grade)"
                  >
                    {{ grade }}
                  </v-chip>
                </div>
              </div>
            </div>
          </v-expand-transition>

          <p v-if="seedFilterHint" class="excel-filter-hint excel-filter-hint--seed">
            <v-icon size="14" class="mr-1">mdi-filter-check</v-icon>
            현재 검색 조건이 적용되었습니다: {{ seedFilterHint }}
          </p>
          <p v-else class="excel-filter-hint">
            <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>
            조건을 선택하지 않으면 전체 시설이 대상입니다.
          </p>

          <div class="excel-preview-panel">
            <p class="excel-filter-hint excel-columns-lead">
              이 조건에 걸리는 시설입니다. 앞에서 8곳만 보여 줍니다.
              <template v-if="excelOverLimit">파일에는 최대 1만 곳이 들어갑니다.</template>
            </p>
            <p v-if="filterPreviewLoading" class="excel-preview-empty">미리보기를 불러오는 중...</p>
            <p v-else-if="previewCount === 0 && !previewLoading" class="excel-preview-empty">조건에 맞는 시설이 없습니다.</p>
            <div v-else-if="filterPreviewRows.length" class="excel-preview-scroll">
              <table class="excel-preview-table">
                <thead>
                  <tr>
                    <th v-for="key in filterPreviewKeys" :key="key">{{ filterPreviewHeaders[key] }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in filterPreviewRows" :key="rowIndex">
                    <td v-for="key in filterPreviewKeys" :key="key">{{ previewCell(row[key]) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-show="excelTab === 'columns'" class="excel-tab-panel excel-columns-panel">
          <p class="excel-filter-hint excel-columns-lead">엑셀에 들어갈 내용을 고르세요. 추천을 누르면 한꺼번에 골라집니다.</p>
          <div class="excel-presets">
            <span class="excel-presets-label">추천</span>
            <button
              v-for="preset in columnPresets"
              :key="preset.key"
              type="button"
              class="excel-preset"
              :class="{ active: activePreset === preset.key }"
              @click="applyPreset(preset.key)"
            >
              {{ preset.label }}
            </button>
          </div>
          <div class="excel-column-groups">
            <div
              v-for="group in columnGroups"
              :key="group.key"
              class="column-group-block"
            >
              <div class="column-group-head">
                <span class="column-group-title">{{ group.label }}</span>
                <span class="column-group-count">
                  {{ getGroupSelectedCount(group.key) }}/{{ (excelColumns[group.key] || []).length }}
                </span>
              </div>
              <div class="column-chip-box">
                <button
                  type="button"
                  class="column-toggle-all"
                  :class="{ active: isGroupAllSelected(group.key) }"
                  @click="toggleGroupAll(group.key)"
                >
                  {{ isGroupAllSelected(group.key) ? '전체 해제' : '전체 선택' }}
                </button>
                <div class="column-chip-list">
                  <v-chip
                    v-for="col in excelColumns[group.key]"
                    :key="col.key"
                    size="small"
                    :class="{ 'chip-selected': selectedColumns.includes(col.key) }"
                    @click="toggleColumn(col.key)"
                  >
                    {{ col.label }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>

          <button type="button" class="excel-order-toggle" @click="showColumnOrder = !showColumnOrder">
            순서 {{ showColumnOrder ? '닫기' : '바꾸기' }}
          </button>
          <div v-if="showColumnOrder && orderedColumns.length > 0" class="excel-column-order-section">
            <div class="order-section-head">
              <v-icon size="16" class="mr-1">mdi-sort</v-icon>
              끌어다 놓으면 엑셀에서의 순서가 바뀝니다
            </div>
            <div class="column-order-list column-order-list--compact">
              <div
                v-for="(col, index) in orderedColumns"
                :key="col.key"
                class="column-order-item"
                draggable="true"
                @dragstart="onDragStart($event, index)"
                @dragover.prevent="onDragOver($event, index)"
                @drop="onDrop($event, index)"
                @dragend="onDragEnd"
                :class="{
                  dragging: dragIndex === index,
                  'drag-over': dragOverIndex === index,
                  'just-moved': justMovedIndices.includes(index)
                }"
              >
                <div class="order-number">{{ index + 1 }}</div>
                <v-icon size="16" class="drag-handle">mdi-drag</v-icon>
                <span class="column-label">{{ col.label }}</span>
              </div>
            </div>
          </div>

          <div class="excel-preview-panel">
            <p class="excel-filter-hint excel-columns-lead">
              고른 내용이 엑셀에 이렇게 들어갑니다. 앞에서 8곳만 보여 줍니다.
              <template v-if="excelOverLimit">파일에는 최대 1만 곳이 들어갑니다.</template>
            </p>
            <p v-if="selectedColumns.length === 0" class="excel-preview-empty">받을 내용을 먼저 골라 주세요.</p>
            <p v-else-if="previewSampleLoading" class="excel-preview-empty">미리보기를 불러오는 중...</p>
            <p v-else-if="previewCount === 0" class="excel-preview-empty">조건에 맞는 시설이 없습니다.</p>
            <div v-else class="excel-preview-scroll">
              <table class="excel-preview-table">
                <thead>
                  <tr>
                    <th v-for="key in previewColumnKeys" :key="key">{{ columnLabels[key] || key }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in previewRows" :key="rowIndex">
                    <td v-for="key in previewColumnKeys" :key="key">{{ previewCell(row[key]) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="excel-modal-footer">
        <div class="footer-summary">
          <div
            class="footer-count"
            :class="{ 'has-data': previewCount > 0 && !excelOverLimit, 'is-capped': excelOverLimit, loading: previewLoading }"
          >
            <v-progress-circular v-if="previewLoading" size="20" width="2" indeterminate class="mr-2" />
            <v-icon v-else-if="excelOverLimit" size="20" color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
            <v-icon v-else-if="previewCount > 0" size="20" color="success" class="mr-2">mdi-check-circle</v-icon>
            <v-icon v-else size="20" color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span v-if="previewLoading">건수 확인 중...</span>
            <span v-else-if="previewCount > 0">
              <strong>{{ previewCount.toLocaleString() }}</strong>개 시설 · 내용 {{ selectedColumns.length }}가지
            </span>
            <span v-else>조건에 맞는 시설 없음</span>
          </div>
          <p v-if="excelOverLimit" class="footer-limit">
            조건에 맞는 시설이 1만 건을 넘습니다. 파일에는 최대 1만 건만 들어갑니다.
          </p>
        </div>
        <div class="footer-actions">
          <v-btn variant="text" class="btn-cancel" @click="closeDialog">취소</v-btn>
          <v-btn variant="flat" class="btn-reset" @click="resetExcelFilters">
            <v-icon size="16" class="mr-1">mdi-refresh</v-icon>
            초기화
          </v-btn>
          <v-btn
            variant="flat"
            class="btn-save excel-download-btn"
            :loading="downloadLoading"
            :disabled="selectedColumns.length === 0 || previewCount === 0 || previewLoading"
            @click="downloadExcel"
          >
            <v-icon class="mr-1" size="18">mdi-download</v-icon>
            다운로드
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { crawlApi } from '@/services/api';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

export interface CrawlExcelSeedFilters {
  sidoCodes?: string[];
  sigunguCode?: string | null;
  bizGubuns?: string[];
  search?: string;
  salesFilter?: string | null;
  total_capacity_min?: number | null;
  total_current_min?: number | null;
  filterHint?: string;
}

const props = withDefaults(defineProps<{
  modelValue: boolean;
  initialFilters?: CrawlExcelSeedFilters | null;
}>(), {
  initialFilters: null,
});

const emit = defineEmits<{
  'update:modelValue': [boolean];
}>();

const store = useStore();

const dialogOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const excelTab = ref('filter' as 'filter' | 'columns');
const filterPreviewKeys = ['cc_name', 'sido_name', 'sigungu_name'];
const filterPreviewHeaders: Record<string, string> = {
  cc_name: '시설명',
  sido_name: '시도',
  sigungu_name: '시군구',
};
const showColumnOrder = ref(false);

const COLUMN_PRESETS: Record<string, string[]> = {
  list: ['cc_name', 'sido_name', 'sigungu_name', 'cc_biz_gubun_short', 'total_capacity', 'total_current', 'cc_tell'],
  contact: ['cc_name', 'sido_name', 'sigungu_name', 'cc_biz_gubun_short', 'cc_addr', 'cc_tell', 'cc_fax', 'cc_email'],
  staff: [
    'cc_name', 'sido_name', 'sigungu_name', 'cc_biz_gubun_short', 'cc_tell',
    'staff_total', 'staff_director', 'staff_social_worker', 'staff_nurse', 'staff_nurse_aid',
    'staff_caregiver1', 'staff_caregiver2', 'staff_pt', 'staff_ot', 'staff_dietitian', 'staff_cook', 'staff_clerk', 'staff_other',
  ],
};

const columnPresets = [
  { key: 'list', label: '목록' },
  { key: 'contact', label: '연락처' },
  { key: 'staff', label: '인력' },
];

const DEFAULT_EXCEL_COLUMNS = COLUMN_PRESETS.list;

const salesFilterOptions = [
  { label: '영업중', value: 'active' },
  { label: '회원가입성공', value: 'completed' },
  { label: '미영업', value: 'none' },
];

const excelColumns = ref<any>({ basic: [], capacity: [], staff: [] });
const selectedColumns = ref<string[]>([]);
const orderedColumns = ref<{ key: string; label: string }[]>([]);
const sidoOptions = ref<any[]>([]);
const sigunguOptions = ref<any[]>([]);
const sigunguLoading = ref(false);
const bizGubunMap = ref<any[]>([]);

const dragIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);
const justMovedIndices = ref<number[]>([]);
const movedDirection = ref<Record<string, 'up' | 'down' | null>>({});

const buildEmptyFilters = () => ({
  sidoCodes: [] as string[],
  sigunguCode: null as string | null,
  bizGubuns: [] as string[],
  search: '' as string,
  salesFilter: null as string | null,
  evalGrades: [] as string[],
  staff_total_min: null as number | null,
  staff_total_max: null as number | null,
  total_capacity_min: null as number | null,
  total_capacity_max: null as number | null,
  total_current_min: null as number | null,
  total_current_max: null as number | null,
  occupancy_rate_min: null as number | null,
  occupancy_rate_max: null as number | null,
});

const excelFilters = ref(buildEmptyFilters());
const EXCEL_ROW_LIMIT = 10000;
const previewCount = ref(0);
const previewRows = ref<Record<string, string | number>[]>([]);
const filterPreviewRows = ref<Record<string, string | number>[]>([]);
const previewSampleLoading = ref(false);
const filterPreviewLoading = ref(false);
const excelOverLimit = computed(() => !previewLoading.value && previewCount.value > EXCEL_ROW_LIMIT);
const previewLoading = ref(false);
const downloadLoading = ref(false);
const showAdvancedFilters = ref(false);
let previewDebounceTimer: number | null = null;
let previewRequestId = 0;

const FALLBACK_BIZ_OPTIONS = [
  { label: '요양시설', value: '요양시설', color: '#0d47a1' },
  { label: '주야간보호', value: '주야간보호', color: '#F57C00' },
  { label: '방문요양', value: '방문요양', color: '#2E7D32' },
  { label: '방문목욕', value: '방문목욕', color: '#00897B' },
  { label: '방문간호', value: '방문간호', color: '#D32F2F' },
  { label: '공동생활가정', value: '공동생활가정', color: '#1565c0' },
  { label: '복지용구', value: '복지용구', color: '#607D8B' },
];

const excelBizOptions = computed(() => {
  if (bizGubunMap.value.length) {
    return bizGubunMap.value.map((m: any) => ({
      label: m.cbm_short,
      value: m.cbm_short,
      color: m.cbm_color || '#607d8b',
    }));
  }
  return FALLBACK_BIZ_OPTIONS;
});

const seedFilterHint = computed(() => props.initialFilters?.filterHint || '');

const activePreset = computed(() => {
  const current = orderedColumns.value.map((col) => col.key).join('|');
  const found = Object.entries(COLUMN_PRESETS).find(([, keys]) => keys.join('|') === current);
  return found ? found[0] : '';
});

/** 시군구 선택 가능한 단일 시도 코드 */
const selectedSidoForSigungu = computed(() => {
  const codes = excelFilters.value.sidoCodes || [];
  return codes.length === 1 ? codes[0] : null;
});

const activeFilterCount = computed(() => {
  let count = 0;
  const f = excelFilters.value;
  if (f.sidoCodes?.length) count++;
  if (f.sigunguCode) count++;
  if (f.bizGubuns?.length) count++;
  if (f.search?.trim()) count++;
  if (f.salesFilter) count++;
  if (f.evalGrades?.length) count++;
  if (f.staff_total_min != null || f.staff_total_max != null) count++;
  if (f.total_capacity_min != null || f.total_capacity_max != null) count++;
  if (f.total_current_min != null || f.total_current_max != null) count++;
  if (f.occupancy_rate_min != null || f.occupancy_rate_max != null) count++;
  return count;
});

const columnGroups = [
  { key: 'basic', label: '기본정보' },
  { key: 'capacity', label: '정원/현원' },
  { key: 'staff', label: '인력현황' },
] as const;

const columnLabels: Record<string, string> = {
  cc_number: '기관번호', cc_name: '기관명', cc_biz_gubun_short: '사업구분',
  cc_addr: '주소', sido_name: '시도', sigungu_name: '시군구',
  cc_tell: '전화번호', cc_fax: '팩스번호', cc_email: '이메일',
  cc_web: '홈페이지', cc_since: '지정일자', cc_registered: '설치신고일자',
  cc_hours: '운영시간', cc_parking: '주차시설', cc_integrated: '통합재가급여',
  eval_grade: '평가등급',
  total_capacity: '총 정원', total_current: '총 현원',
  total_waiting: '총 대기자', total_available: '총 이용가능',
  occupancy_rate: '입소율(%)',
  staff_total: '총 인원', staff_director: '시설장',
  staff_social_worker: '사회복지사', staff_nurse: '간호사',
  staff_nurse_aid: '간호조무사', staff_caregiver1: '요양보호사1급',
  staff_caregiver2: '요양보호사2급', staff_pt: '물리치료사',
  staff_ot: '작업치료사', staff_dietitian: '영양사',
  staff_cook: '조리원', staff_clerk: '사무원', staff_other: '기타',
};

const closeDialog = () => {
  dialogOpen.value = false;
};

const switchExcelTab = (tab: 'filter' | 'columns') => {
  excelTab.value = tab;
  if (tab === 'filter') fetchFilterPreview();
  if (tab === 'columns') fetchPreviewSample();
};

const applySeedToFilters = () => {
  const base = buildEmptyFilters();
  const seed = props.initialFilters;
  if (!seed) {
    excelFilters.value = base;
    return;
  }
  if (seed.sidoCodes?.length) base.sidoCodes = [...seed.sidoCodes];
  if (seed.sigunguCode) base.sigunguCode = seed.sigunguCode;
  if (seed.bizGubuns?.length) base.bizGubuns = [...seed.bizGubuns];
  if (seed.search?.trim()) base.search = seed.search.trim();
  if (seed.salesFilter) base.salesFilter = seed.salesFilter;
  if (seed.total_capacity_min != null) base.total_capacity_min = Number(seed.total_capacity_min);
  if (seed.total_current_min != null) base.total_current_min = Number(seed.total_current_min);
  excelFilters.value = base;

  if (base.total_capacity_min != null || base.total_current_min != null) {
    showAdvancedFilters.value = true;
  }
};

const loadSigunguOptions = async (sidoCode: string) => {
  sigunguLoading.value = true;
  try {
    const res = await crawlApi.getSigunguList(sidoCode);
    sigunguOptions.value = res.data?.data || [];
    if (
      excelFilters.value.sigunguCode
      && !sigunguOptions.value.some((sg: any) => sg.sigungu_code === excelFilters.value.sigunguCode)
    ) {
      excelFilters.value.sigunguCode = null;
    }
  } catch (error: any) {
    sigunguOptions.value = [];
    const message = error.response?.data?.message || error.message || '시군구 조회 실패';
    store.commit('showSnackbar', { text: message, color: 'error' });
  } finally {
    sigunguLoading.value = false;
  }
};

const ensureOptionsLoaded = async () => {
  const tasks: Promise<void>[] = [];
  if (!sidoOptions.value.length) {
    tasks.push(
      crawlApi.getSidoList().then((res) => {
        if (res.data.success) sidoOptions.value = res.data.data;
      }),
    );
  }
  if (!bizGubunMap.value.length) {
    tasks.push(
      crawlApi.getBizGubunList().then((res) => {
        bizGubunMap.value = res.data?.data || [];
      }),
    );
  }
  if (tasks.length) await Promise.all(tasks);
};

const initDialog = async () => {
  try {
    await ensureOptionsLoaded();

    const res = await crawlApi.getExcelColumns();
    if (res.data.success) {
      excelColumns.value = res.data.data;
      selectedColumns.value = [...DEFAULT_EXCEL_COLUMNS];
      orderedColumns.value = [];
      syncOrderedColumns();
    }

    applySeedToFilters();
    previewRows.value = [];
    filterPreviewRows.value = [];
    excelTab.value = 'filter';
    if (selectedSidoForSigungu.value) {
      await loadSigunguOptions(selectedSidoForSigungu.value);
    } else {
      sigunguOptions.value = [];
    }
    previewCount.value = 0;
    if (excelFilters.value.total_capacity_min == null && excelFilters.value.total_current_min == null) {
      showAdvancedFilters.value = false;
    }
    excelTab.value = 'filter';
    showColumnOrder.value = false;

    fetchPreviewCount();
    fetchFilterPreview();
  } catch (error: any) {
    console.error('엑셀 컬럼 조회 오류:', error);
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    store.commit('showSnackbar', { text: `받을 내용을 불러오지 못했습니다: ${message}`, color: 'error' });
    closeDialog();
  }
};

const getColumnLabel = (key: string): string => {
  for (const group of ['basic', 'capacity', 'staff'] as const) {
    const found = excelColumns.value[group]?.find((c: any) => c.key === key);
    if (found) return found.label;
  }
  return columnLabels[key] || key;
};

const syncOrderedColumns = () => {
  const kept = orderedColumns.value.filter(c => selectedColumns.value.includes(c.key));
  const keptKeys = new Set(kept.map(c => c.key));
  const added = selectedColumns.value
    .filter(k => !keptKeys.has(k))
    .map(k => ({ key: k, label: getColumnLabel(k) }));
  orderedColumns.value = [...kept, ...added];
};

const toggleColumn = (key: string) => {
  const idx = selectedColumns.value.indexOf(key);
  if (idx === -1) {
    selectedColumns.value = [...selectedColumns.value, key];
  } else {
    selectedColumns.value = selectedColumns.value.filter(k => k !== key);
  }
};

const getGroupSelectedCount = (group: string) => {
  const cols = excelColumns.value[group] || [];
  return cols.filter((col: any) => selectedColumns.value.includes(col.key)).length;
};

const isGroupAllSelected = (group: string) => {
  const groupColumns = excelColumns.value[group] || [];
  if (groupColumns.length === 0) return false;
  return groupColumns.every((col: any) => selectedColumns.value.includes(col.key));
};

const toggleGroupAll = (group: string) => {
  const groupColumns = excelColumns.value[group] || [];
  const groupKeys = groupColumns.map((col: any) => col.key);

  if (isGroupAllSelected(group)) {
    selectedColumns.value = selectedColumns.value.filter(key => !groupKeys.includes(key));
  } else {
    const newKeys = groupKeys.filter((key: string) => !selectedColumns.value.includes(key));
    selectedColumns.value = [...selectedColumns.value, ...newKeys];
  }
};

const toggleSidoFilter = (sidoCode: string) => {
  const idx = excelFilters.value.sidoCodes?.indexOf(sidoCode);
  if (idx === -1 || idx === undefined) {
    excelFilters.value.sidoCodes = [...(excelFilters.value.sidoCodes || []), sidoCode];
  } else {
    excelFilters.value.sidoCodes = excelFilters.value.sidoCodes.filter((c: string) => c !== sidoCode);
  }
  if (excelFilters.value.sidoCodes?.length !== 1) {
    excelFilters.value.sigunguCode = null;
    sigunguOptions.value = [];
  }
};

const selectAllSido = () => {
  if (excelFilters.value.sidoCodes?.length === sidoOptions.value.length) {
    excelFilters.value.sidoCodes = [];
  } else {
    excelFilters.value.sidoCodes = sidoOptions.value.map((s: any) => s.sido_code);
  }
  excelFilters.value.sigunguCode = null;
  sigunguOptions.value = [];
};

const toggleSigunguFilter = (sigunguCode: string) => {
  excelFilters.value.sigunguCode = excelFilters.value.sigunguCode === sigunguCode ? null : sigunguCode;
};

const clearSigunguFilter = () => {
  excelFilters.value.sigunguCode = null;
};

const setSalesFilter = (value: string) => {
  excelFilters.value.salesFilter = excelFilters.value.salesFilter === value ? null : value;
};

const applyPreset = (key: string) => {
  const keys = COLUMN_PRESETS[key];
  if (!keys) return;
  selectedColumns.value = [...keys];
  orderedColumns.value = [];
  syncOrderedColumns();
};

const toggleBizGubunFilter = (bizGubun: string) => {
  const idx = excelFilters.value.bizGubuns?.indexOf(bizGubun);
  if (idx === -1 || idx === undefined) {
    excelFilters.value.bizGubuns = [...(excelFilters.value.bizGubuns || []), bizGubun];
  } else {
    excelFilters.value.bizGubuns = excelFilters.value.bizGubuns.filter((b: string) => b !== bizGubun);
  }
};

const selectAllBizGubun = () => {
  const opts = excelBizOptions.value;
  if (excelFilters.value.bizGubuns?.length === opts.length) {
    excelFilters.value.bizGubuns = [];
  } else {
    excelFilters.value.bizGubuns = opts.map(b => b.value);
  }
};

const toggleEvalGradeFilter = (grade: string) => {
  const idx = excelFilters.value.evalGrades?.indexOf(grade);
  if (idx === -1 || idx === undefined) {
    excelFilters.value.evalGrades = [...(excelFilters.value.evalGrades || []), grade];
  } else {
    excelFilters.value.evalGrades = excelFilters.value.evalGrades.filter((g: string) => g !== grade);
  }
};

const setStaffRange = (min: number | null, max: number | null) => {
  excelFilters.value.staff_total_min = min;
  excelFilters.value.staff_total_max = max;
};

const setStaffRangeOver51 = () => setStaffRange(51, null);

const resetExcelFilters = () => {
  excelFilters.value = buildEmptyFilters();
  sigunguOptions.value = [];
  selectedColumns.value = [...DEFAULT_EXCEL_COLUMNS];
  orderedColumns.value = [];
  syncOrderedColumns();
  showAdvancedFilters.value = false;
  fetchPreviewCount();
};

const fetchPreviewCount = async () => {
  const currentRequestId = ++previewRequestId;

  try {
    previewLoading.value = true;
    const filterSnapshot = JSON.parse(JSON.stringify(excelFilters.value));
    const res = await crawlApi.previewExcel(filterSnapshot);

    if (currentRequestId !== previewRequestId) return;

    if (res.data.success) {
      previewCount.value = res.data.data.count || 0;
    }
  } catch (error: any) {
    if (currentRequestId === previewRequestId) {
      previewCount.value = 0;
      const message = error.response?.data?.message || error.message || '건수 조회 실패';
      store.commit('showSnackbar', { text: message, color: 'error' });
    }
  } finally {
    if (currentRequestId === previewRequestId) {
      previewLoading.value = false;
    }
  }
};

const debouncedFetchPreviewCount = () => {
  if (previewDebounceTimer) clearTimeout(previewDebounceTimer);
  previewDebounceTimer = window.setTimeout(() => {
    fetchPreviewCount();
    if (excelTab.value === 'filter') fetchFilterPreview();
    if (excelTab.value === 'columns') fetchPreviewSample();
  }, 500);
};

const getDownloadColumnKeys = (): string[] => {
  if (orderedColumns.value.length > 0) {
    return orderedColumns.value.map(col => col.key);
  }
  return selectedColumns.value;
};

const previewColumnKeys = computed(() => getDownloadColumnKeys());

const previewCell = (value: string | number | null | undefined) => {
  if (value == null || value === '') return '';
  return String(value);
};

let previewSampleRequestId = 0;
let filterPreviewRequestId = 0;

const fetchFilterPreview = async () => {
  const currentRequestId = ++filterPreviewRequestId;
  try {
    filterPreviewLoading.value = true;
    const filterSnapshot = JSON.parse(JSON.stringify(excelFilters.value));
    const res = await crawlApi.previewExcel(filterSnapshot, [...filterPreviewKeys]);
    if (currentRequestId !== filterPreviewRequestId) return;
    if (res.data.success) {
      previewCount.value = res.data.data.count || 0;
      filterPreviewRows.value = res.data.data.rows || [];
    }
  } catch (error: any) {
    if (currentRequestId === filterPreviewRequestId) {
      filterPreviewRows.value = [];
      const message = error.response?.data?.message || error.message || '미리보기를 불러오지 못했습니다.';
      store.commit('showSnackbar', { text: message, color: 'error' });
    }
  } finally {
    if (currentRequestId === filterPreviewRequestId) {
      filterPreviewLoading.value = false;
    }
  }
};

const fetchPreviewSample = async () => {
  const keys = getDownloadColumnKeys();
  const currentRequestId = ++previewSampleRequestId;
  if (keys.length === 0) {
    previewRows.value = [];
    previewSampleLoading.value = false;
    return;
  }

  try {
    previewSampleLoading.value = true;
    const filterSnapshot = JSON.parse(JSON.stringify(excelFilters.value));
    const res = await crawlApi.previewExcel(filterSnapshot, keys);
    if (currentRequestId !== previewSampleRequestId) return;
    if (res.data.success) {
      previewCount.value = res.data.data.count || 0;
      previewRows.value = res.data.data.rows || [];
    }
  } catch (error: any) {
    if (currentRequestId === previewSampleRequestId) {
      previewRows.value = [];
      const message = error.response?.data?.message || error.message || '미리보기를 불러오지 못했습니다.';
      store.commit('showSnackbar', { text: message, color: 'error' });
    }
  } finally {
    if (currentRequestId === previewSampleRequestId) {
      previewSampleLoading.value = false;
    }
  }
};

const onDragStart = (event: DragEvent, index: number) => {
  dragIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
  }
};

const onDragOver = (event: DragEvent, index: number) => {
  event.preventDefault();
  dragOverIndex.value = index;
};

const onDrop = (event: DragEvent, index: number) => {
  event.preventDefault();
  if (dragIndex.value === null || dragIndex.value === index) return;

  const fromIndex = dragIndex.value;
  const toIndex = index;
  const columns = [...orderedColumns.value];

  const originalIndices: Record<string, number> = {};
  columns.forEach((col, idx) => {
    originalIndices[col.key] = idx;
  });

  const [removed] = columns.splice(fromIndex, 1);
  columns.splice(toIndex, 0, removed);
  orderedColumns.value = columns;

  const minIdx = Math.min(fromIndex, toIndex);
  const maxIdx = Math.max(fromIndex, toIndex);
  justMovedIndices.value = [];
  for (let i = minIdx; i <= maxIdx; i++) {
    justMovedIndices.value.push(i);
  }

  movedDirection.value = {};
  columns.forEach((col, newIdx) => {
    const oldIdx = originalIndices[col.key];
    if (newIdx < oldIdx) movedDirection.value[col.key] = 'up';
    else if (newIdx > oldIdx) movedDirection.value[col.key] = 'down';
  });

  setTimeout(() => {
    justMovedIndices.value = [];
    movedDirection.value = {};
  }, 800);

  dragIndex.value = null;
  dragOverIndex.value = null;
};

const onDragEnd = () => {
  dragIndex.value = null;
  dragOverIndex.value = null;
};

const downloadExcel = async () => {
  const keys = getDownloadColumnKeys();
  if (keys.length === 0) {
    store.commit('showSnackbar', { text: '받을 내용을 하나 이상 골라 주세요.', color: 'warning' });
    excelTab.value = 'columns';
    return;
  }
  if (previewCount.value === 0) {
    store.commit('showSnackbar', { text: '조건에 맞는 시설이 없습니다.', color: 'warning' });
    return;
  }

  try {
    downloadLoading.value = true;
    await crawlApi.downloadExcel(keys, excelFilters.value);
    store.commit('showSnackbar', { text: '엑셀 다운로드가 완료되었습니다.', color: 'success' });
    closeDialog();
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '다운로드 오류';
    store.commit('showSnackbar', { text: `엑셀 다운로드 실패: ${message}`, color: 'error' });
  } finally {
    downloadLoading.value = false;
  }
};

watch(() => props.modelValue, (open) => {
  if (open) initDialog();
});

watch(selectedSidoForSigungu, async (sidoCode) => {
  if (!dialogOpen.value) return;
  if (!sidoCode) {
    sigunguOptions.value = [];
    return;
  }
  await loadSigunguOptions(sidoCode);
});

watch(excelFilters, () => {
  if (dialogOpen.value) debouncedFetchPreviewCount();
}, { deep: true });

watch(selectedColumns, () => {
  if (!dialogOpen.value) return;
  syncOrderedColumns();
  if (excelTab.value === 'columns') fetchPreviewSample();
}, { deep: true });
</script>
